import type { ComponentProps } from '@enonic/react-components'
import type { ResponsiveImageData } from '../primitives'
import type { CtaButton } from '../../utils/ctaUtils'
import * as React from 'react'
import { ResponsiveImage, ButtonLink } from '../primitives'
import { Icon } from '/react4xp/components/primitives/Icon'

export interface EducationFinderData extends Record<string, unknown> {
  baseUrl: string
  categories: { label: string; slug: string }[]
}

export interface FrontPageData extends Record<string, unknown> {
  title: string
  image?: ResponsiveImageData
  ctaPrimary?: CtaButton
  ctaSecondary?: CtaButton
  educationFinder?: EducationFinderData
}

export const FrontPage = (props: ComponentProps) => {
  const data = props.data as FrontPageData
  const { title, image, ctaPrimary, ctaSecondary, educationFinder } = data

  const [selectedCategory, setSelectedCategory] = React.useState('')

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value)
  }

  const finderReady = educationFinder?.baseUrl ? educationFinder : undefined

  const baseUrl = educationFinder?.baseUrl ?? '/utdanninger'
  const linkHref = selectedCategory
    ? baseUrl + '?kategori=' + selectedCategory
    : baseUrl

  return (
    <section
      data-hero
      className="relative min-h-[max(700px,80svh)] overflow-hidden"
    >
      {image && (
        <div aria-hidden="true" className="absolute inset-0">
          <ResponsiveImage
            data={image}
            className="h-full w-full object-cover"
            fetchpriority="high"
          />
        </div>
      )}

      <div
        aria-hidden="true"
        className="hero__overlay absolute inset-0 mix-blend-multiply"
      />

      <div className="relative flex min-h-[inherit] flex-col justify-end">
        {/* Title + CTAs */}
        <div className="mx-auto w-full max-w-container px-5 pb-8 md:px-16 md:pb-14">
          <div className="flex max-w-xl flex-col gap-6 md:max-w-2xl md:gap-9">
            <h1 className="font-serif text-4xl leading-tightest text-white md:text-7xl">
              {title}
            </h1>

            {(ctaPrimary || ctaSecondary) && (
              <div className="flex gap-2 md:gap-3">
                {ctaPrimary && (
                  <ButtonLink
                    href={ctaPrimary.url}
                    label={ctaPrimary.label}
                    variant="button-white"
                  />
                )}
                {ctaSecondary && (
                  <ButtonLink
                    href={ctaSecondary.url}
                    label={ctaSecondary.label}
                    variant="button"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Education finder — blue box anchored bottom-left, content aligned to container */}
        {finderReady && (
          <div className="relative">
            <div
              className="absolute inset-y-0 left-0 w-full bg-blue-900 md:w-full lg:w-[75vw] xl:w-[55vw]"
              aria-hidden="true"
            />
            <div className="relative mx-auto w-full max-w-container px-5 pb-8 pt-6 md:px-16 md:pb-8 md:pt-6">
              <div className="flex flex-col gap-5">
                <h2 className="text-2xl text-white md:text-3xl">
                  Finn utdanning som passer deg
                </h2>
                <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-4">
                  <div className="relative">
                    <label
                      htmlFor="education-finder-category"
                      className="sr-only"
                    >
                      Velg utdanningstilbud
                    </label>
                    <select
                      id="education-finder-category"
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      className="h-10 w-full appearance-none border border-white bg-transparent px-3 pr-10 text-sm text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white [&>option]:bg-white [&>option]:text-black md:h-12 md:w-72 md:text-xl"
                    >
                      <option value="">Velg utdanningstilbud</option>
                      {finderReady.categories.map((cat) => (
                        <option key={cat.slug} value={cat.slug}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    <Icon
                      name="keyboard_arrow_down"
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white"
                    />
                  </div>
                  <a
                    href={linkHref}
                    className="flex h-10 items-center justify-center bg-blue-500 px-6 text-sm uppercase tracking-wider text-black no-underline transition-colors hover:bg-blue-300 focus-visible:outline-1 focus-visible:outline-slate-300 md:h-12"
                  >
                    Se utdanninger
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
